import React, { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { Sidebar } from 'rsuite'
import Sidenav from 'rsuite/Sidenav'
import Nav from 'rsuite/Nav'
import {
  getMenuSectionsForRole,
  getMenuItemByPath,
  MenuSectionType
} from 'config/MenuItems'
import { useAuth } from 'contexts/AuthContext'
import NavItem from './NavItem'
import './sideNav.scss'

interface SideNavProps {
  mobileOpen?: boolean
  onClose?: () => void
}

const SideNav: React.FC<SideNavProps> = ({ mobileOpen = false, onClose }) => {
  const location = useLocation()
  const { role } = useAuth()
  const [activeKey, setActiveKey] = React.useState('')
  const hideSideNav = location.pathname === '/user/kyc'

  const menuSections = useMemo<MenuSectionType[]>(
    () => getMenuSectionsForRole(role),
    [role]
  )

  useEffect(() => {
    const activeItem = getMenuItemByPath(location.pathname, role)
    setActiveKey(activeItem?.id ?? '')
  }, [location.pathname, role])

  if (!menuSections.length) {
    return null
  }

  const handleNavSelect = (eventKey: string | number | undefined) => {
    if (typeof eventKey === 'string') {
      setActiveKey(eventKey)
    }
    onClose?.()
  }

  if (hideSideNav) {
    return null
  }

  return (
    <>
      {mobileOpen && (
        <div
          className="sidebar-overlay"
          onClick={onClose}
          role="presentation"
        />
      )}
      <Sidebar
        className={`admin-sidebar${mobileOpen ? ' admin-sidebar--open' : ''}`}
        data-testid="sidebar"
      >
        <div className="sideNav">
          <Sidenav appearance="subtle" expanded>
            <Sidenav.Body>
              {menuSections.map((section) => (
                <section className="sideNav__section" key={section.id}>
                  <h4 className="sideNav__section-title">{section.title}</h4>
                  <Nav activeKey={activeKey} onSelect={handleNavSelect}>
                    {section.items.map(({ id, name, link, icon }) => (
                      <NavItem
                        key={id}
                        id={id}
                        name={name}
                        link={link}
                        icon={icon}
                        active={activeKey === id}
                      />
                    ))}
                  </Nav>
                </section>
              ))}
            </Sidenav.Body>
          </Sidenav>
        </div>
      </Sidebar>
    </>
  )
}

export default SideNav
